/**
 * AIVideoButton — Standalone header button for one-click AI video generation.
 *
 * Displays a videocam icon with an "AI" badge. When a diagram has just been
 * generated (diagramJustGeneratedAtom), the badge pulses and a tooltip nudges
 * the user to click. While video generation is in progress, shows a spinner
 * with elapsed time.
 */

import React, { useCallback, useEffect, useState } from "react";

import { useAtom } from "../app-jotai";
import { useAppI18n } from "../hooks/useAppI18n";
import { diagramJustGeneratedAtom } from "../hooks/useAIVideoSignal";

import "./AIVideoButton.scss";

/** Format seconds as M:SS */
const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

interface AIVideoButtonProps {
  hasCanvasElements: boolean;
  isLoading: boolean;
  elapsedSeconds: number;
  onOpenVideoSettings: () => void;
}

export const AIVideoButton: React.FC<AIVideoButtonProps> = ({
  hasCanvasElements,
  isLoading,
  elapsedSeconds,
  onOpenVideoSettings,
}) => {
  const { t } = useAppI18n();
  const [diagramJustGenerated, setDiagramJustGenerated] = useAtom(
    diagramJustGeneratedAtom,
  );
  const [showNudge, setShowNudge] = useState(false);

  useEffect(() => {
    if (!diagramJustGenerated) {
      setShowNudge(false);
      return;
    }
    setShowNudge(true);
    const id = setTimeout(() => {
      setShowNudge(false);
      setDiagramJustGenerated(false);
    }, 8000);
    return () => clearTimeout(id);
  }, [diagramJustGenerated, setDiagramJustGenerated]);

  const handleClick = useCallback(() => {
    setShowNudge(false);
    setDiagramJustGenerated(false);
    onOpenVideoSettings();
  }, [onOpenVideoSettings, setDiagramJustGenerated]);

  if (isLoading) {
    return (
      <span
        className="ai-video-btn ai-video-btn--loading"
        title={t("aiVideo.title")}
      >
        <span className="material-symbols-outlined ai-video-btn__spinner">
          progress_activity
        </span>
        <span className="ai-video-btn__time">{formatTime(elapsedSeconds)}</span>
      </span>
    );
  }

  return (
    <div className="ai-video-btn-wrap">
      <button
        type="button"
        className={`ai-video-btn${showNudge ? " ai-video-btn--glow" : ""}`}
        onClick={handleClick}
        disabled={!hasCanvasElements}
        title={t("aiVideo.title")}
      >
        <span className="material-symbols-outlined ai-video-btn__icon">
          videocam
        </span>
        <span
          className={`ai-video-btn__badge${showNudge ? " ai-video-btn__badge--pulse" : ""}`}
        >
          AI
        </span>
      </button>

      {showNudge && (
        <div className="ai-video-btn__nudge">
          <span className="ai-video-btn__nudge-arrow" />
          <span className="ai-video-btn__nudge-text">
            {t("aiVideo.tooltip")}
          </span>
        </div>
      )}
    </div>
  );
};
